import { AlertCircle, AlertTriangle, Info } from "lucide-react";
import { cn } from "@/shared/lib/utils";

type Severity = "info" | "warn" | "risk";

interface SectionCalloutProps {
  severity: Severity;
  body: string;
  title?: string;
}

const TONE: Record<
  Severity,
  { Icon: typeof Info; bg: string; border: string; text: string }
> = {
  info: {
    Icon: Info,
    // Use the alpha-on-base-hue pattern so info reads as a tinted card on
    // the dark surface. The token --status-info-bg is `217 91% 95%` (near
    // white) which painted as a giant white block on the dark theme.
    bg: "bg-status-info/10",
    border: "border-status-info/40",
    text: "text-status-info",
  },
  warn: {
    Icon: AlertTriangle,
    bg: "bg-status-warning/10",
    border: "border-status-warning/40",
    text: "text-status-warning",
  },
  risk: {
    Icon: AlertCircle,
    bg: "bg-status-critical/10",
    border: "border-status-critical/40",
    text: "text-status-critical",
  },
};

/**
 * Pull-quote alert that frames a specific insight (gap, anomaly, risk). Uses
 * the existing --status-* tokens so the tone matches the rest of the surface.
 */
export function SectionCallout({ severity, body, title }: SectionCalloutProps) {
  const t = TONE[severity];
  const Icon = t.Icon;
  return (
    <div
      className={cn(
        "flex items-start gap-3 rounded-lg border px-5 py-4",
        t.bg,
        t.border,
      )}
    >
      <span className={cn("mt-0.5 shrink-0", t.text)}>
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1 space-y-1">
        {title ? (
          <div className={cn("text-sm font-semibold", t.text)}>{title}</div>
        ) : null}
        <div className="text-sm text-foreground/90">{body}</div>
      </div>
    </div>
  );
}
